import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import PasswordStrength, { isPasswordStrong } from "@/components/PasswordStrength";

export default function ChangePasswordDialog({ open, onOpenChange, onSuccess }) {
    const [currentPw, setCurrentPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [busy, setBusy] = useState(false);

    const reset = () => {
        setCurrentPw("");
        setNewPw("");
        setConfirmPw("");
    };

    const submit = async () => {
        if (!isPasswordStrong(newPw)) {
            toast.error("Neues Passwort erfüllt die Regeln nicht");
            return;
        }
        if (newPw !== confirmPw) {
            toast.error("Die beiden neuen Passwörter stimmen nicht überein");
            return;
        }
        setBusy(true);
        try {
            await api.put("/auth/change-password", {
                current_password: currentPw,
                new_password: newPw,
            });
            toast.success("Passwort geändert. Bitte neu anmelden.");
            reset();
            onOpenChange?.(false);
            onSuccess?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Passwortwechsel fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange?.(o); }}>
            <DialogContent className="max-w-md" data-testid="change-password-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading flex items-center gap-2">
                        <KeyRound className="h-4 w-4" /> Passwort ändern
                    </DialogTitle>
                    <DialogDescription>
                        Zur Bestätigung geben Sie bitte Ihr aktuelles Passwort ein. Nach dem Wechsel werden Sie erneut zur Anmeldung aufgefordert.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Aktuelles Passwort</Label>
                        <Input
                            type="password"
                            value={currentPw}
                            onChange={(e) => setCurrentPw(e.target.value)}
                            data-testid="change-pw-current"
                            autoFocus
                        />
                    </div>
                    <div>
                        <Label>Neues Passwort</Label>
                        <Input
                            type="password"
                            value={newPw}
                            onChange={(e) => setNewPw(e.target.value)}
                            data-testid="change-pw-new"
                        />
                        <PasswordStrength pw={newPw} />
                    </div>
                    <div>
                        <Label>Neues Passwort wiederholen</Label>
                        <Input
                            type="password"
                            value={confirmPw}
                            onChange={(e) => setConfirmPw(e.target.value)}
                            data-testid="change-pw-confirm"
                        />
                        {confirmPw && confirmPw !== newPw && (
                            <p className="text-[11px] text-red-300 mt-1">Passwörter stimmen nicht überein</p>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange?.(false)}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={busy || !currentPw || !isPasswordStrong(newPw) || newPw !== confirmPw}
                        className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                        data-testid="change-pw-submit"
                    >
                        {busy ? "Speichere…" : "Passwort ändern"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
